import { Injectable, inject } from "@angular/core";
import { createAuth0Client, type Auth0Client } from "@auth0/auth0-spa-js";

import { environment } from "../../../environments/environment";
import { AuthStateService } from "../services/auth-state.service";
import { BrandingService } from "../services/branding.service";
import { readAuthRefusal } from "./read-auth-refusal";
import { TENANT_CONTEXT } from "./tenant-context.token";

import type { IAuthProvider, IAuthRefusal } from "./interfaces";

/**
 * Auth0 provider for customer users. The organization comes from the slug in
 * the hostname, so a user only ever authenticates against their own tenant.
 *
 * The login screen itself is Auth0's Universal Login, branded to look like the
 * portal (infrastructure/auth0/universal-login) — this app owns only the
 * redirect interstitial. Customers are created directly with a temporary
 * password, so there is no invitation to redeem here.
 */
@Injectable({ providedIn: "root" })
export class Auth0PortalAuthService implements IAuthProvider {

  private readonly authState = inject(AuthStateService);

  private readonly tenant = inject(TENANT_CONTEXT);

  private readonly brandingService = inject(BrandingService);

  private client: Auth0Client | null = null;

  /**
   * Auth0's `organization` parameter must be the org ID, not the org name.
   * Passing the slug is rejected outright with "authorization request
   * parameter organization must be an organization id", so every login fails.
   *
   * The SPA only knows the slug from its hostname; the id arrives with the
   * branding, which is fetched pre-auth for exactly this reason.
   */
  private get organization(): string | undefined {
    return this.brandingService.branding()
.organizationId ?? undefined;
  }

  /**
   * Refuses to start a login this host cannot complete. A customer subdomain
   * with no organization id would otherwise be sent to Auth0 only to bounce
   * back with an opaque `invalid_request`; failing here says why.
   */
  private assertOrganizationResolved(): void {
    if (this.tenant.kind === "auth0" && this.organization === undefined) {
      throw new Error(
        `No Auth0 organization is provisioned for "${this.tenant.organization}".`
      );
    }
  }

  /**
   * `window.location.origin` drops the querystring, which silently breaks the
   * local-dev flow: on localhost the tenant comes from `?org=<slug>`, so
   * returning to a bare origin resolves to the Entra apex context instead of
   * the customer. Preserve the override across the round trip.
   *
   * Only `org` is carried, and only when it is already present — a deployed
   * host takes its tenant from the subdomain and is unaffected.
   */
  private redirectUri(): string {
    const params = new URLSearchParams(window.location.search);

    const org: string | null = params.get("org");

    return org !== null && org.length > 0
      ? `${window.location.origin}/?org=${encodeURIComponent(org)}`
      : `${window.location.origin}/`;
  }

  private async getClient(): Promise<Auth0Client> {
    if (this.client === null) {
      this.client = await createAuth0Client({
        domain: environment.auth0.domain,
        clientId: environment.auth0.clientId,
        authorizationParams: {
          audience: environment.auth0.audience,
          organization: this.organization,
          redirect_uri: this.redirectUri(),
        },
        cacheLocation: "localstorage",
      });
    }

    return this.client;
  }

  /** Drop the callback's querystring but keep `?org=` — see redirectUri(). */
  private stripCallbackParams(search: string): void {
    const org: string | null = new URLSearchParams(search)
.get("org");
    const kept: string =
      org !== null && org.length > 0 ? `?org=${encodeURIComponent(org)}` : "";
    window.history.replaceState({}, "", `${window.location.pathname}${kept}`);
  }

  public async initialize(): Promise<void> {
    const client: Auth0Client = await this.getClient();
    const search: string = window.location.search;

    // Auth0 came back with an error instead of a code — most often an account
    // that is not a member of this host's organization. Record it and stop.
    // A silent token attempt here would only send Auth0 the same refused
    // account again, and the guards then route on the refusal instead of
    // redirecting to Auth0 for another round.
    const refusal: IAuthRefusal | null = readAuthRefusal(search);
    if (refusal !== null) {
      console.warn("customer-portal: Auth0 refused the login", refusal.description);
      this.stripCallbackParams(search);
      this.authState.clear();
      this.authState.setRefusal(refusal);
      return;
    }

    if (search.includes("code=") && search.includes("state=")) {
      try {
        await client.handleRedirectCallback();
        // Strip the auth code but keep ?org= — dropping it here would undo
        // redirectUri()'s work on the very next navigation.
        this.stripCallbackParams(search);
      } catch {
        // Fall through to the silent attempt; the callback may simply be stale.
      }
    }

    try {
      await client.getTokenSilently();
    } catch {
      // No session. Guards send the user to /login.
      this.authState.clear();
      return;
    }

    const claims = await client.getIdTokenClaims();
    if (claims === undefined) {
      this.authState.clear();
      return;
    }

    this.authState.setFromClaims(claims as unknown as Record<string, unknown>);
  }

  public async login(): Promise<void> {
    this.assertOrganizationResolved();
    const client: Auth0Client = await this.getClient();

    await client.loginWithRedirect({
      authorizationParams: {
        audience: environment.auth0.audience,
        organization: this.organization,
        redirect_uri: this.redirectUri(),
      },
    });
  }

  public async logout(): Promise<void> {
    const client: Auth0Client = await this.getClient();
    this.authState.clear();
    await client.logout({ logoutParams: { returnTo: window.location.origin } });
  }

  public async getAccessToken(): Promise<string | null> {
    try {
      const client: Auth0Client = await this.getClient();
      return await client.getTokenSilently();
    } catch {
      return null;
    }
  }

  public async refreshAccessToken(): Promise<string | null> {
    try {
      const client: Auth0Client = await this.getClient();
      // cacheMode "off" is the whole point: getTokenSilently() serves the
      // cached token until it EXPIRES, so without this a stale customer claim
      // would be re-sent on every retry and the session would stay broken
      // until the token aged out on its own. Going back to Auth0 re-runs the
      // post-login Action, which reads the organization metadata again and
      // mints the corrected claim.
      return await client.getTokenSilently({ cacheMode: "off" });
    } catch {
      return null;
    }
  }
}
